import React, { MouseEventHandler } from "react";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Box } from "@pokemon/design-v2.box";
import { Button } from "@pokemon/design-v2.button";

export interface ModalProps extends DialogProps {
  /**
   * required field that handles accessibility for the modal
   */
  id: string;
  /**
   * optional title for the modal, rendered as an h2, with a matching aria-labelledby prop on the modal
   */
  title?: string;
  /**
   * text for the primary button, also causes the primary button to render if truthy
   */
  primaryButtonText?: string;
  /**
   * text for the secondary button that closes the modal, also causes the primary button to render if truthy
   */
  secondaryButtonText?: string;
  /**
   * what to do when someone clicks the primary modal button - defaults to calling onClose
   */
  primaryOnClick?: MouseEventHandler;
  /**
   * what to do when someone clicks the secondary modal button - defaults to calling onClose
   */
  secondaryOnClick?: MouseEventHandler;
  /**
   * function to close the modal
   */
  onClose: MouseEventHandler;
  /**
   * optional prop for a logo to be displayed at the top of the modal
   */
  logo?: React.ReactNode;
  /**
   * optional prop rendering the modal full screen
   */
  fullScreen?: boolean;
  /**
   * optional prop rendering the modal buttons full screen
   */
  fullWidthButton?: boolean;
  /**
   * CSS class of button, either 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'. Defaults to "primary".
   */
  buttonStatus?:
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "warning"
    | "info";
}

export const Modal = ({
  id,
  title,
  primaryButtonText,
  secondaryButtonText,
  primaryOnClick,
  secondaryOnClick,
  onClose,
  children,
  logo,
  fullScreen,
  fullWidthButton,
  buttonStatus = "primary",
  ...props
}: ModalProps) => (
  <Dialog
    aria-labelledby={`${id}-title`}
    aria-describedby={`${id}-description`}
    fullScreen={fullScreen}
    onClose={onClose}
    {...props}
  >
    <DialogTitle id={`${id}-title`}>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText id={`${id}-description`}>
        {logo && (
          <Box display="flex" justifyContent="center" marginBottom="8%">
            {logo}
          </Box>
        )}
        {children}
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      {fullScreen || fullWidthButton ? (
        <Box sx={{ width: "100%" }}>
          {primaryButtonText && (
            <Button
              onClick={primaryOnClick || onClose}
              buttonStatus={buttonStatus}
              fullWidth
            >
              {primaryButtonText}
            </Button>
          )}
          {secondaryButtonText && (
            <Button
              onClick={secondaryOnClick || onClose}
              sx={{ marginTop: "2vh" }}
              variant="text"
              buttonStatus={buttonStatus}
              fullWidth
            >
              {secondaryButtonText}
            </Button>
          )}
        </Box>
      ) : (
        <>
          {secondaryButtonText && (
            <Button
              onClick={secondaryOnClick || onClose}
              buttonStatus={buttonStatus}
              variant="text"
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              onClick={primaryOnClick || onClose}
              buttonStatus={buttonStatus}
            >
              {primaryButtonText}
            </Button>
          )}
        </>
      )}
    </DialogActions>
  </Dialog>
);
